import MaintenanceIcon from '@/assets/smartcity/maintenance-icon.svg';
import OfflineIcon from '@/assets/smartcity/offline-icon.svg';
import { useCctvStream } from '@/domains/traffic-monitoring/hooks/useCctvStream';
import { cn } from '@/lib/utils';
import { useColorStore } from '@/store/colorStore';

interface VideoCardProps {
  className?: string;
  typeVideo: 'online' | 'offline' | 'maintenance';
  /** HLS (.m3u8) URL already resolved by the backend - played via hls.js. */
  streamUrl?: string | null;
  isHighRes?: boolean;
  location?: string;
  showLocation?: boolean;
  onClick?: () => void;
}

const VideoCard = ({
  showLocation = true,
  typeVideo,
  className,
  streamUrl,
  isHighRes = false,
  location,
  onClick,
}: VideoCardProps) => {
  const { NEWCOLOR } = useColorStore();
  const { videoRef, status } = useCctvStream({ streamUrl, isHighRes });

  const statusConfig = {
    online: {
      text: 'Online',
      color: NEWCOLOR.greenPrimary,
      bg: '#1D293D80',
      icon: null,
    },
    offline: {
      text: 'Offline',
      color: NEWCOLOR.danger,
      bg: '#FC66051A',
      icon: OfflineIcon,
    },
    maintenance: {
      text: 'Maintenance',
      color: NEWCOLOR.warning,
      bg: '#FC66051A',
      icon: MaintenanceIcon,
    },
  };

  const statusInfo = statusConfig[typeVideo];

  const showVideo = typeVideo === 'online' && !!streamUrl;
  const isBuffering = showVideo && (status === 'loading' || status === 'reconnecting');

  return (
    <div className={cn('relative w-full pt-[56.25%] shrink-0', className, 'h-auto')}>
      <button
        type="button"
        onClick={onClick}
        className="absolute inset-0 h-full w-full overflow-hidden rounded-xl border bg-db-border cursor-pointer"
      >
        <div
          className="absolute left-2 top-2 z-10 inline-flex items-center gap-2 rounded-full border px-3 py-1 backdrop-blur-sm"
          style={{
            backgroundColor: statusInfo.bg,
            borderColor: statusInfo.color,
          }}
        >
          <div
            className="h-2 w-2 rounded-full"
            style={{
              backgroundColor: statusInfo.color,
            }}
          />

          <span
            className="text-xs font-semibold uppercase"
            style={{
              color: statusInfo.color,
            }}
          >
            {statusInfo.text}
          </span>
        </div>

        {showVideo ? (
          <>
            {/* biome-ignore lint/a11y/useMediaCaption: live camera feed, no captions source */}
            <video ref={videoRef} muted playsInline className="h-full w-full object-cover" />
            {isBuffering && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                <span className="text-xs text-white">
                  {status === 'reconnecting' ? 'Reconnecting…' : 'Connecting…'}
                </span>
              </div>
            )}
          </>
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-db-border">
            {statusInfo.icon ? (
              <img src={statusInfo.icon} alt={statusInfo.text} className="h-20 w-20" />
            ) : (
              <span className="text-sm text-db-grey">No Video</span>
            )}
          </div>
        )}

        {showLocation && location && (
          <div
            className="absolute max-w-[90%] bottom-2 left-2 z-10 inline-flex items-center gap-2 rounded-[4px] px-3 py-1 backdrop-blur-sm"
            style={{
              backgroundColor: NEWCOLOR.bluePrimary,
            }}
          >
            <span className="truncate text-[clamp(0.7rem,1.2vw,1rem)] text-white capitalize">
              {location}
            </span>
          </div>
        )}
      </button>
    </div>
  );
};

export default VideoCard;
